import { useEffect, useRef, useState } from 'react';
import {
  IonButtons,
  IonContent,
  IonHeader,
  IonLabel,
  IonModal,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { ConnectionsList } from './ConnectionsList';
import { ConnectionRequests } from './ConnectionRequests';
import { SentConnectionRequests } from './SentConnectionRequests';
import { useMyConnections } from '../../hooks/useConnections';

interface ConnectionsListModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabValue = 'connections' | 'pending';

// Stable reference so Ionic doesn't re-init the sheet gesture mid-animation
// (a fresh array each render snaps the sheet closed — see ActivityListModal).
const SHEET_BREAKPOINTS = [0, 0.6, 0.9];

/**
 * Pull-up sheet that holds the connections surface, split into two tabs:
 *   - Connections: the accepted-connections list.
 *   - Pending: incoming requests (actionable) + requests you've sent.
 *
 * Defaults to the Connections tab, EXCEPT when there's an actionable incoming
 * request waiting on the caller (a non-empty `requests_to_me`) — then it opens
 * on Pending so the thing needing a response is front and center. A request the
 * caller *sent* (pending on the other person) is not actionable and doesn't
 * flip the default. Each child owns its own data fetch.
 */
export const ConnectionsListModal: React.FC<ConnectionsListModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('connections');

  // Incoming requests decide the default tab; sent requests only factor into
  // the Pending tab's empty state. Both share query keys with the child
  // panels, so TanStack dedupes — no extra network.
  const { data: incoming } = useMyConnections('requests_to_me');
  const { data: sent } = useMyConnections('requests_from_me');
  const hasActionable = (incoming?.length ?? 0) > 0;
  const pendingEmpty =
    (incoming?.length ?? 0) === 0 && (sent?.length ?? 0) === 0;

  const [activeTab, setActiveTab] = useState<TabValue>('connections');
  // Pick the default once per open. A ref (not state) avoids re-running the
  // decision after the user manually switches tabs while the sheet is open.
  const didInit = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      didInit.current = false;
      return;
    }
    if (didInit.current) return;
    // Wait until we know whether there's an actionable request before locking
    // in the default, so a slow first fetch doesn't land on Connections and
    // then jump. If the data is already cached this resolves on the open frame.
    if (incoming === undefined) return;
    setActiveTab(hasActionable ? 'pending' : 'connections');
    didInit.current = true;
  }, [isOpen, incoming, hasActionable]);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      breakpoints={SHEET_BREAKPOINTS}
      initialBreakpoint={0.9}
      handle
      // Let IonContent scroll at non-max breakpoints instead of the drag
      // gesture owning vertical movement (see ActivityListModal).
      expandToScroll={false}
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <button
              type="button"
              onClick={onClose}
              className="px-4 text-sm font-medium text-brand-primary"
            >
              {t('list.done')}
            </button>
          </IonButtons>
          {/* Absolutely positioned so the start button can't push the title
              off-center — Ionic lays the title in normal flow after the
              buttons otherwise (same fix as ActivityListModal). */}
          <IonTitle
            className="ion-text-center"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              pointerEvents: 'none',
            }}
          >
            {t('list.title')}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="p-6 space-y-4">
          <IonSegment
            value={activeTab}
            onIonChange={(e) => setActiveTab(e.detail.value as TabValue)}
          >
            <IonSegmentButton value="connections">
              <IonLabel>{t('tabs.connections')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="pending">
              <IonLabel>
                {hasActionable
                  ? t('tabs.pendingCount', { count: incoming?.length ?? 0 })
                  : t('tabs.pending')}
              </IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {activeTab === 'connections' ? (
            <ConnectionsList />
          ) : (
            <div className="space-y-4">
              {/* Each panel renders nothing when its list is empty; the
                  fallback covers the case where the whole tab is empty. */}
              <ConnectionRequests />
              <SentConnectionRequests />
              {pendingEmpty && (
                <p className="text-sm text-muted-foreground">
                  {t('pending.empty')}
                </p>
              )}
            </div>
          )}
        </div>
      </IonContent>
    </IonModal>
  );
};
